/**
 * TAX_DIFFERS_FROM_TICKETS — the API's non-blocking warning on POST /invoices/generate.
 *
 * Shape (api, feat/invoice-tax-differs-warning): the envelope is
 * `{ success, data: {...invoice, ticketCount}, warnings: [...] }`, with
 * `warnings` a top-level sibling of `data`, `[]` when there is nothing to say.
 * Each warning is camelCase, money as JS numbers to the cent:
 *   { code, message, ticketTax, ticketTotal, invoiceTax, invoiceTotal,
 *     difference (invoiceTotal minus ticketTotal, signed),
 *     tickets: [{ ticketNumber, ticketTotal, invoiceTotal }] }
 *
 * The invoice IS created when this warning arrives. Every figure here was
 * computed by the API; this module finds the warning and reads its fields. It
 * recomputes nothing — not the difference, not either total (non-negotiable 5).
 * A figure the API did not send stays `null` and renders as an em dash, never 0.
 */

export const TAX_DIFFERS_FROM_TICKETS = 'TAX_DIFFERS_FROM_TICKETS';

export interface InvoiceTaxWarning {
  message: string | null;
  ticketTotal: number | null;
  invoiceTotal: number | null;
  difference: number | null;
  ticketNumbers: string[];
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * Find the warning on a generate response body (`res.data`). Returns null when
 * the response carries no such warning, and then the screen shows nothing new.
 */
export function readTaxDiffersWarning(body: unknown): InvoiceTaxWarning | null {
  const warnings = (body as { warnings?: unknown } | null)?.warnings;
  if (!Array.isArray(warnings)) return null;
  const w = warnings.find((x) => x && typeof x === 'object' && (x as { code?: unknown }).code === TAX_DIFFERS_FROM_TICKETS);
  if (!w) return null;
  const tickets: unknown[] = Array.isArray(w.tickets) ? w.tickets : [];
  return {
    message: typeof w.message === 'string' && w.message.trim() ? w.message : null,
    ticketTotal: num(w.ticketTotal),
    invoiceTotal: num(w.invoiceTotal),
    difference: num(w.difference),
    ticketNumbers: tickets
      .map((t) => (t && typeof t === 'object' ? (t as { ticketNumber?: unknown }).ticketNumber : null))
      .filter((n): n is string | number => (typeof n === 'string' && n.trim() !== '') || typeof n === 'number')
      .map(String),
  };
}
